import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  Handshake, 
  LayoutDashboard, 
  Users, 
  DollarSign, 
  Settings, 
  LogOut,
  Link as LinkIcon,
  ChevronDown,
  Copy,
  Check,
  LifeBuoy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const NAV_ITEMS = [
  { path: '/partner/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/partner/customers', icon: Users, label: 'Customers' },
  { path: '/partner/billing', icon: DollarSign, label: 'Billing' },
  { path: '/partner/support', icon: LifeBuoy, label: 'Support' },
];

export default function PartnerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [partner, setPartner] = useState(null);
  const [copied, setCopied] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = "Connect | Partner Portal";
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('partner_token');
    const partnerData = localStorage.getItem('partner');
    
    if (!token || !partnerData) {
      navigate('/partner/login');
      return;
    }
    
    try {
      setPartner(JSON.parse(partnerData));
    } catch (e) {
      // Invalid partner data - clear and redirect
      localStorage.removeItem('partner_token');
      localStorage.removeItem('partner');
      navigate('/partner/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('partner_token');
    localStorage.removeItem('partner');
    navigate('/partner/login');
    toast.success('Logged out successfully');
  };

  const copyReferralLink = () => {
    if (partner?.referral_code) {
      const link = `${window.location.origin}/signup?ref=${partner.referral_code}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!partner) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-64 border-r border-border/50 flex flex-col h-screen fixed left-0 top-0 bg-card">
        {/* Logo */}
        <div className="p-6 border-b border-border/50">
          <div className="flex items-center gap-3">
            <img src="/connect-logo.png" alt="CONNECT" className="w-10 h-10 rounded-lg" />
            <div>
              <h1 className="font-bold text-lg">CONNECT</h1>
              <p className="text-xs text-muted-foreground">Partner Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
                      isActive
                        ? "bg-primary/10 text-primary border border-primary/30"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
          
          {/* Referral Link Quick Copy */}
          <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center gap-2 text-sm font-medium mb-2">
              <LinkIcon className="w-4 h-4 text-primary" />
              Your Referral Link
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full justify-between"
              onClick={copyReferralLink}
            >
              <span className="truncate text-xs font-mono">
                ...?ref={partner.referral_code}
              </span>
              {copied ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-border/50">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-between h-auto py-3 px-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <Handshake className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium truncate max-w-[120px]">
                      {partner.company_name}
                    </p>
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-500 border-green-500/30">
                      Active
                    </Badge>
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="font-medium">{partner.contact_name}</span>
                  <span className="text-xs text-muted-foreground">{partner.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/partner/settings')} className="cursor-pointer">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 overflow-auto">
        <Outlet context={{ partner }} />
      </main>
    </div>
  );
}
